// Typed client of sculpt-api: the session routes and every /admin route (sculpt-api/README.md "Admin").
// One transparent refresh-and-retry on 401; every other failure is an ApiError with the API's `detail`.
// The API opens /admin only to a Microsoft session from the Peerception tenant (sculpt-api accounts SPEC), so there
// is no password sign-in here.
import { clearTokens, readToken } from './tokenStore';
import { BASE_URL, refreshAccessToken, logout as authLogout } from './refreshAuth';
import type {
  AuditEvent,
  BillingState,
  CreditGrant,
  CreditPackAdmin,
  CreditPackIn,
  CreditPackPatch,
  CreditsOverview,
  CurrencyIn,
  CurrencyOut,
  PriceTable,
  PricesIn,
  EuDistanceSales,
  VatDecision,
  VatReview,
  MigrateResult,
  OfferAdmin,
  OfferCreate,
  OfferPatch,
  OrderAdmin,
  MonthlyBilling,
  PaymentAdmin,
  RefundAdmin,
  RefundIn,
  OrdersQuery,
  Terms,
  TermsResult,
  AuditQuery,
  Claim,
  ConversationDetail,
  IssueCreate,
  IssueDetail,
  IssueLinkIn,
  IssuePatch,
  IssueRow,
  IssuesQuery,
  ConversationRow,
  ConversationsQuery,
  Insights,
  InsightsQuery,
  Invitation,
  InvoiceAdmin,
  InvoiceCreate,
  InvoicePatch,
  InvoicesQuery,
  MailQuery,
  Me,
  OrganizationAdmin,
  OrganizationAdminPatch,
  OrganizationCreate,
  OrganizationDetail,
  OrganizationsQuery,
  Outbox,
  OutboxDetail,
  WaitlistQuery,
  WaitlistSignup,
  ContactRequest,
  ContactRequestPatch,
  ContactRequestsQuery,
  Page,
  Stats,
  UserAdmin,
  UserAdminPatch,
  UserCreate,
  UsersQuery, ReplayOut, ReplayGolden, Sandbox, SandboxDetail, SandboxEvent, SandboxOpenIn, SandboxUpdateIn, AgentMode,
} from '../types/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body: string,
  ) {
    super(`API ${status}: ${detail || body}`);
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

/** The API's `detail` out of an error body: a string as is, anything else as JSON, '' when the body is not JSON. */
function parseDetail(body: string): string {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    return typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail ?? '');
  } catch {
    return '';
  }
}

async function detailOf(res: Response): Promise<string> {
  return parseDetail(await res.text().catch(() => ''));
}

function qs(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export class ApiClient {
  private unauthorizedListeners = new Set<() => void>();

  constructor(private host: string = BASE_URL) {}

  /** Called whenever an authenticated call still answers 401 once the refresh has had its chance, or an /admin call
   *  answers 403 `staff_only` (a session the API no longer takes for staff: one opened before it asked for the
   *  Peerception tenant, or a staff flag removed): the session is over, and the app goes back to /login. Returns an
   *  unsubscribe function. */
  onUnauthorized(listener: () => void): () => void {
    this.unauthorizedListeners.add(listener);
    return () => this.unauthorizedListeners.delete(listener);
  }

  /** Absolute URL of the Microsoft sign-in start for this client (a full-page navigation). */
  microsoftLoginUrl(returnTo: string): string {
    const base = new URL(this.host.endsWith('/') ? this.host.slice(0, -1) : this.host, window.location.origin);
    return `${base.toString().replace(/\/$/, '')}/login/microsoft?client=backoffice&return_to=${encodeURIComponent(returnTo)}`;
  }

  private authHeader(): Record<string, string> {
    const token = readToken('accessToken');
    const type = readToken('tokenType');
    return token && type ? { Authorization: `${type} ${token}` } : {};
  }

  private async request(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
    const res = await fetch(`${this.host}${path}`, { ...init, headers: { accept: 'application/json', ...this.authHeader(), ...(init.headers || {}) } });
    if (res.status === 401 && !retried) {
      const ok = await refreshAccessToken();
      if (ok) return this.request(path, init, true);
    }
    if (res.status === 401) this.unauthorizedListeners.forEach((l) => l());
    if (res.status === 403 && path.startsWith('/admin') && (await detailOf(res.clone())) === 'staff_only') {
      clearTokens();
      this.unauthorizedListeners.forEach((l) => l());
    }
    return res;
  }

  private async json<T>(res: Response): Promise<T> {
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, parseDetail(body), body);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private body(method: string, body?: unknown): RequestInit {
    return { method, headers: body === undefined ? {} : { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) };
  }

  private get<T>(path: string, query?: Query): Promise<T> {
    return this.request(path + qs(query)).then((r) => this.json<T>(r));
  }

  private send<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.request(path, this.body(method, body)).then((r) => this.json<T>(r));
  }

  // ---- session ----
  me(): Promise<Me> {
    return this.get<Me>('/user');
  }

  logout(): Promise<void> {
    return authLogout();
  }

  // ---- stats ----
  stats(): Promise<Stats> {
    return this.get<Stats>('/admin/stats');
  }

  // ---- users ----
  users(query: UsersQuery = {}): Promise<Page<UserAdmin>> {
    return this.get<Page<UserAdmin>>('/admin/users', query as Query);
  }

  user(id: number): Promise<UserAdmin> {
    return this.get<UserAdmin>(`/admin/users/${id}`);
  }

  createUser(body: UserCreate): Promise<UserAdmin> {
    return this.send<UserAdmin>('POST', '/admin/users', body);
  }

  patchUser(id: number, body: UserAdminPatch): Promise<UserAdmin> {
    return this.send<UserAdmin>('PATCH', `/admin/users/${id}`, body);
  }

  /** The person's objection to the review's sample, recorded on their request, or its withdrawal (audited). */
  setReviewObjection(id: number, objected: boolean): Promise<UserAdmin> {
    return this.send<UserAdmin>('PUT', `/admin/users/${id}/review-objection`, { objected });
  }

  sendReset(id: number): Promise<{ sent: boolean }> {
    return this.send<{ sent: boolean }>('POST', `/admin/users/${id}/reset-password`);
  }

  moveUser(id: number, organization_id: number, role: string): Promise<UserAdmin> {
    return this.send<UserAdmin>('POST', `/admin/users/${id}/move`, { organization_id, role });
  }

  deleteUser(id: number): Promise<void> {
    return this.send<void>('DELETE', `/admin/users/${id}`);
  }

  // ---- organizations ----
  organizations(query: OrganizationsQuery = {}): Promise<Page<OrganizationAdmin>> {
    return this.get<Page<OrganizationAdmin>>('/admin/organizations', query as Query);
  }

  organization(id: number): Promise<OrganizationDetail> {
    return this.get<OrganizationDetail>(`/admin/organizations/${id}`);
  }

  createOrganization(body: OrganizationCreate): Promise<OrganizationAdmin> {
    return this.send<OrganizationAdmin>('POST', '/admin/organizations', body);
  }

  patchOrganization(id: number, body: OrganizationAdminPatch): Promise<OrganizationAdmin> {
    return this.send<OrganizationAdmin>('PATCH', `/admin/organizations/${id}`, body);
  }

  // ---- VAT numbers VIES could not answer for (sculpt-api PLAN-payments.md P9) ----
  vatReviews(): Promise<VatReview[]> {
    return this.get<VatReview[]>('/admin/vat-reviews');
  }

  /** Settle the organization's VAT number: valid → reverse charge, not valid → French VAT. Held orders become payable. */
  decideVat(id: number, valid: boolean): Promise<VatDecision> {
    return this.send<VatDecision>('POST', `/admin/organizations/${id}/vat`, { valid });
  }

  /** Ask VIES again now; an answer settles a number under review. */
  checkVat(id: number): Promise<VatDecision> {
    return this.send<VatDecision>('POST', `/admin/organizations/${id}/vat/check`);
  }

  euDistanceSales(year?: number): Promise<EuDistanceSales> {
    return this.get<EuDistanceSales>('/admin/vat/eu-distance-sales', { year });
  }

  inviteToOrganization(id: number, email: string, role: string): Promise<Invitation> {
    return this.send<Invitation>('POST', `/admin/organizations/${id}/invitations`, { email, role });
  }

  revokeInvitation(id: number, invitationId: number): Promise<void> {
    return this.send<void>('DELETE', `/admin/organizations/${id}/invitations/${invitationId}`);
  }

  verifyClaim(id: number, claimId: number): Promise<Claim> {
    return this.send<Claim>('POST', `/admin/organizations/${id}/claims/${claimId}/verify`);
  }

  deleteClaim(id: number, claimId: number): Promise<void> {
    return this.send<void>('DELETE', `/admin/organizations/${id}/claims/${claimId}`);
  }

  transferOwnership(id: number, user_id: number): Promise<void> {
    return this.send<void>('POST', `/admin/organizations/${id}/transfer`, { user_id });
  }

  // ---- billing: offers, packs, subscriptions, credits, orders (PLAN-offers.md §8) ----
  offers(): Promise<OfferAdmin[]> {
    return this.get<OfferAdmin[]>('/admin/offers');
  }

  offer(id: number): Promise<OfferAdmin> {
    return this.get<OfferAdmin>(`/admin/offers/${id}`);
  }

  createOffer(body: OfferCreate): Promise<OfferAdmin> {
    return this.send<OfferAdmin>('POST', '/admin/offers', body);
  }

  patchOffer(id: number, body: OfferPatch): Promise<OfferAdmin> {
    return this.send<OfferAdmin>('PATCH', `/admin/offers/${id}`, body);
  }

  /** New terms: the current version edited while unused, else the next version (subscribers keep theirs). */
  setTerms(id: number, terms: Terms): Promise<TermsResult> {
    return this.send<TermsResult>('PUT', `/admin/offers/${id}/terms`, terms);
  }

  migrateVersion(versionId: number, to_version_id: number, effective_from: string | null): Promise<MigrateResult> {
    return this.send<MigrateResult>('POST', `/admin/offer-versions/${versionId}/migrate`, { to_version_id, effective_from });
  }

  packs(): Promise<CreditPackAdmin[]> {
    return this.get<CreditPackAdmin[]>('/admin/credit-packs');
  }

  createPack(body: CreditPackIn): Promise<CreditPackAdmin> {
    return this.send<CreditPackAdmin>('POST', '/admin/credit-packs', body);
  }

  patchPack(id: number, body: CreditPackPatch): Promise<CreditPackAdmin> {
    return this.send<CreditPackAdmin>('PATCH', `/admin/credit-packs/${id}`, body);
  }

  /** Every currency staff set, EUR first (fixed at 1). */
  currencies(): Promise<CurrencyOut[]> {
    return this.get<CurrencyOut[]>('/admin/currencies');
  }

  /** A currency's rate and whether it is on sale, created on first use; returns the full list. */
  setCurrency(code: string, body: CurrencyIn): Promise<CurrencyOut[]> {
    return this.send<CurrencyOut[]>('PUT', `/admin/currencies/${encodeURIComponent(code)}`, body);
  }

  /** Remove a currency nothing uses; refused for EUR and USD, one on sale and one in use (`currency_in_use`). */
  deleteCurrency(code: string): Promise<CurrencyOut[]> {
    return this.send<CurrencyOut[]>('DELETE', `/admin/currencies/${encodeURIComponent(code)}`);
  }

  /** Every offer and pack key, priced in each currency, with the currencies. */
  priceTable(): Promise<PriceTable> {
    return this.get<PriceTable>('/admin/price-table');
  }

  /** One currency's column of the price table (all or nothing); returns the new table. */
  setPrices(currency: string, body: PricesIn): Promise<PriceTable> {
    return this.send<PriceTable>('PUT', `/admin/price-table/${encodeURIComponent(currency)}`, body);
  }

  setSubscription(orgId: number, offer_version_id: number, seats: number, credit_tier: string | null = null): Promise<BillingState> {
    return this.send<BillingState>('PUT', `/admin/organizations/${orgId}/subscription`, { offer_version_id, seats, credit_tier });
  }

  extendSubscription(orgId: number, days: number): Promise<BillingState> {
    return this.send<BillingState>('POST', `/admin/organizations/${orgId}/subscription/extend`, { days });
  }

  grantCredits(orgId: number, amount_eur: string, expires_at: string | null, note: string): Promise<CreditGrant> {
    return this.send<CreditGrant>('POST', `/admin/organizations/${orgId}/credits`, { amount_eur, expires_at, note });
  }

  orders(query: OrdersQuery = {}): Promise<Page<OrderAdmin>> {
    return this.get<Page<OrderAdmin>>('/admin/orders', query as Query);
  }

  /** An order's card payment attempts through Mollie, newest first (PLAN-payments.md M3). */
  orderPayments(id: number): Promise<PaymentAdmin[]> {
    return this.get<PaymentAdmin[]>(`/admin/orders/${id}/payments`);
  }

  /** An order's refunds, newest first (PLAN-payments.md M5). */
  orderRefunds(id: number): Promise<RefundAdmin[]> {
    return this.get<RefundAdmin[]>(`/admin/orders/${id}/refunds`);
  }

  /** Give money back on a paid order, with a credit note of the amount (M5). */
  refundOrder(id: number, body: RefundIn): Promise<RefundAdmin> {
    return this.send<RefundAdmin>('POST', `/admin/orders/${id}/refunds`, body);
  }

  /** A month's paid orders, refunds and chargebacks in one currency (`month`: YYYY-MM; the API defaults to EUR). */
  monthlyBilling(month: string, currency?: string): Promise<MonthlyBilling> {
    return this.get<MonthlyBilling>('/admin/billing/monthly', currency ? { month, currency } : { month });
  }

  /** Revoke an organization's saved card (its Mollie mandate): renewals are no longer charged on it (PLAN-payments.md M4). */
  revokeCard(orgId: number): Promise<void> {
    return this.send<void>('POST', `/admin/organizations/${orgId}/card/revoke`);
  }

  markOrderPaid(id: number): Promise<OrderAdmin> {
    return this.send<OrderAdmin>('POST', `/admin/orders/${id}/paid`);
  }

  cancelOrder(id: number): Promise<OrderAdmin> {
    return this.send<OrderAdmin>('POST', `/admin/orders/${id}/cancel`);
  }

  invoiceOrder(id: number): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/orders/${id}/invoice`);
  }

  // ---- invoices ----
  invoices(query: InvoicesQuery = {}): Promise<Page<InvoiceAdmin>> {
    return this.get<Page<InvoiceAdmin>>('/admin/invoices', query as Query);
  }

  invoice(id: number): Promise<InvoiceAdmin> {
    return this.get<InvoiceAdmin>(`/admin/invoices/${id}`);
  }

  createInvoice(body: InvoiceCreate): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', '/admin/invoices', body);
  }

  patchInvoice(id: number, body: InvoicePatch): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('PATCH', `/admin/invoices/${id}`, body);
  }

  async uploadInvoicePdf(id: number, file: File): Promise<InvoiceAdmin> {
    const res = await this.request(`/admin/invoices/${id}/pdf?filename=${encodeURIComponent(file.name)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: await file.arrayBuffer(),
    });
    return this.json<InvoiceAdmin>(res);
  }

  /** The PDF as a blob (the route needs the bearer, so no plain link). */
  async downloadInvoicePdf(id: number): Promise<Blob> {
    const res = await this.request(`/admin/invoices/${id}/pdf`, { headers: { accept: 'application/pdf' } });
    if (!res.ok) throw new ApiError(res.status, '', await res.text().catch(() => ''));
    return res.blob();
  }

  sendInvoice(id: number): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/invoices/${id}/send`);
  }

  markInvoicePaid(id: number, paid_at?: string | null): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/invoices/${id}/paid`, { paid_at: paid_at || null });
  }

  voidInvoice(id: number): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/invoices/${id}/void`);
  }

  deleteInvoice(id: number): Promise<void> {
    return this.send<void>('DELETE', `/admin/invoices/${id}`);
  }

  /** A Qonto invoice that failed goes back to the invoicing worker (409 `not_failed` otherwise). */
  retryInvoice(id: number): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/invoices/${id}/retry`);
  }

  /** A credit note for the whole of an issued Qonto invoice; returns the credit note (issued by the worker). */
  creditInvoice(id: number, reason: string): Promise<InvoiceAdmin> {
    return this.send<InvoiceAdmin>('POST', `/admin/invoices/${id}/credit-note`, { reason });
  }

  // ---- credits across organizations (PLAN-offers.md §15.7) ----
  credits(): Promise<CreditsOverview> {
    return this.get<CreditsOverview>('/admin/credits');
  }

  // ---- waitlist ----
  waitlist(query: WaitlistQuery = {}): Promise<Page<WaitlistSignup>> {
    return this.get<Page<WaitlistSignup>>('/admin/waitlist', query as Query);
  }

  // ---- contact requests (the website's contact form) ----
  contactRequests(query: ContactRequestsQuery = {}): Promise<Page<ContactRequest>> {
    return this.get<Page<ContactRequest>>('/admin/contact-requests', query as Query);
  }

  contactRequest(id: number): Promise<ContactRequest> {
    return this.get<ContactRequest>(`/admin/contact-requests/${id}`);
  }

  updateContactRequest(id: number, body: ContactRequestPatch): Promise<ContactRequest> {
    return this.send<ContactRequest>('PATCH', `/admin/contact-requests/${id}`, body);
  }

  // ---- outbox ----
  mail(query: MailQuery = {}): Promise<Page<Outbox>> {
    return this.get<Page<Outbox>>('/admin/mail', query as Query);
  }

  mailDetail(id: number): Promise<OutboxDetail> {
    return this.get<OutboxDetail>(`/admin/mail/${id}`);
  }

  retryMail(id: number): Promise<Outbox> {
    return this.send<Outbox>('POST', `/admin/mail/${id}/retry`);
  }

  // ---- conversation quality ----
  insights(query: InsightsQuery = {}): Promise<Insights> {
    return this.get<Insights>('/admin/insights', query as Query);
  }

  conversations(query: ConversationsQuery = {}): Promise<Page<ConversationRow>> {
    return this.get<Page<ConversationRow>>('/admin/conversations', query as Query);
  }

  /** One conversation with its transcript: the API files an audit row for every reading. A restricted one answers 403
   *  `restricted_conversation` unless `support` (opened for a support request, which the audit row records). */
  conversation(threadId: string, support = false): Promise<ConversationDetail> {
    return this.get<ConversationDetail>(`/admin/conversations/${encodeURIComponent(threadId)}`, support ? { support: true } : undefined);
  }

  /** The conversation's server side played again on the code running now: deviation or no deviation (audited). */
  replayConversation(threadId: string): Promise<ReplayOut> {
    return this.send<ReplayOut>('POST', `/admin/conversations/${encodeURIComponent(threadId)}/replay`);
  }

  /** Freeze what the code produces for this conversation now as its reference: from here on it is an eval (audited). */
  acceptReplay(threadId: string, reason: string | null): Promise<ReplayGolden> {
    return this.send<ReplayGolden>('POST', `/admin/conversations/${encodeURIComponent(threadId)}/replay/accept`, { reason });
  }

  /** A sandbox of the conversation from a turn (audited): its own copy of that turn's workbook version and state. */
  openSandbox(threadId: string, body: SandboxOpenIn): Promise<Sandbox> {
    return this.send<Sandbox>('POST', `/admin/conversations/${encodeURIComponent(threadId)}/sandboxes`, body);
  }

  /** Every sandbox, or one conversation's, newest activity first. */
  sandboxes(threadId?: string): Promise<Sandbox[]> {
    return this.get<Sandbox[]>('/admin/sandboxes', threadId ? { thread_id: threadId } : undefined);
  }

  /** A sandbox with the source's turns, its own conversation and the comparison (audited: it shows a user's conversation). */
  sandbox(id: string): Promise<SandboxDetail> {
    return this.get<SandboxDetail>(`/admin/sandboxes/${encodeURIComponent(id)}`);
  }

  /** Send a recorded user message (default: the next one); the turn runs in the background. */
  playSandbox(id: string, turnIndex?: number): Promise<{ turn_index: number }> {
    return this.send<{ turn_index: number }>('POST', `/admin/sandboxes/${encodeURIComponent(id)}/play`, turnIndex === undefined ? {} : { turn_index: turnIndex });
  }

  /** Send a message typed by staff, in a mode. */
  sandboxMessage(id: string, message: string, mode: AgentMode): Promise<{ ok: boolean }> {
    return this.send<{ ok: boolean }>('POST', `/admin/sandboxes/${encodeURIComponent(id)}/messages`, { message, mode });
  }

  /** Answer what the turn stopped on: answers to a question card, or approve / refuse a confirmation. */
  answerSandbox(id: string, body: { approve: boolean; answers?: unknown[] | null }): Promise<{ ok: boolean }> {
    return this.send<{ ok: boolean }>('POST', `/admin/sandboxes/${encodeURIComponent(id)}/answer`, body);
  }

  cancelSandbox(id: string): Promise<{ ok: boolean }> {
    return this.send<{ ok: boolean }>('POST', `/admin/sandboxes/${encodeURIComponent(id)}/cancel`);
  }

  /** The events streamed since `after`, oldest first. */
  sandboxEvents(id: string, after: number): Promise<SandboxEvent[]> {
    return this.get<SandboxEvent[]>(`/admin/sandboxes/${encodeURIComponent(id)}/events`, { after });
  }

  /** Approve every confirmation from now on, or ask for each one (audited). */
  updateSandbox(id: string, body: SandboxUpdateIn): Promise<Sandbox> {
    return this.send<Sandbox>('PATCH', `/admin/sandboxes/${encodeURIComponent(id)}`, body);
  }

  deleteSandbox(id: string): Promise<void> {
    return this.send<void>('DELETE', `/admin/sandboxes/${encodeURIComponent(id)}`);
  }

  /** Retire the reference: the conversation leaves the corpus (audited). */
  retireReplay(threadId: string): Promise<void> {
    return this.send<void>('DELETE', `/admin/conversations/${encodeURIComponent(threadId)}/replay/accept`);
  }

  /** The conversation as a JSON file (the route needs the bearer, so no plain link); the API audits the export. */
  async downloadConversation(threadId: string, support = false): Promise<Blob> {
    const res = await this.request(`/admin/conversations/${encodeURIComponent(threadId)}/export${support ? qs({ support: true }) : ''}`);
    if (!res.ok) throw new ApiError(res.status, '', await res.text().catch(() => ''));
    return res.blob();
  }

  // ---- issues ----
  issues(query: IssuesQuery = {}): Promise<Page<IssueRow>> {
    return this.get<Page<IssueRow>>('/admin/issues', query as Query);
  }

  issue(id: number): Promise<IssueDetail> {
    return this.get<IssueDetail>(`/admin/issues/${id}`);
  }

  /** Open an issue with the evidence it starts from (404 `ref_not_found`). */
  createIssue(body: IssueCreate): Promise<IssueDetail> {
    return this.send<IssueDetail>('POST', '/admin/issues', body);
  }

  /** Change the fields sent; one timeline event per field changed (409 `wrong_duplicate`, `owner_not_staff`). */
  patchIssue(id: number, body: IssuePatch): Promise<IssueDetail> {
    return this.send<IssueDetail>('PATCH', `/admin/issues/${id}`, body);
  }

  linkIssue(id: number, link: IssueLinkIn): Promise<IssueDetail> {
    return this.send<IssueDetail>('POST', `/admin/issues/${id}/links`, link);
  }

  unlinkIssue(id: number, linkId: number): Promise<IssueDetail> {
    return this.send<IssueDetail>('DELETE', `/admin/issues/${id}/links/${linkId}`);
  }

  commentIssue(id: number, body: string): Promise<IssueDetail> {
    return this.send<IssueDetail>('POST', `/admin/issues/${id}/comments`, { body });
  }

  /** The issue as Markdown for Claude: the issue, its history, its conversations' transcripts. The API audits each
   *  conversation it carries. */
  async issueMarkdown(id: number): Promise<string> {
    const res = await this.request(`/admin/issues/${id}/export`);
    if (!res.ok) throw new ApiError(res.status, '', await res.text().catch(() => ''));
    return res.text();
  }

  /** Its author edits a comment (403 `not_author`). */
  editIssueComment(id: number, commentId: number, body: string): Promise<IssueDetail> {
    return this.send<IssueDetail>('PATCH', `/admin/issues/${id}/comments/${commentId}`, { body });
  }

  // ---- audit ----
  audit(query: AuditQuery = {}): Promise<Page<AuditEvent>> {
    return this.get<Page<AuditEvent>>('/admin/audit', query as Query);
  }
}

export const api = new ApiClient();
